import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, ScoreRing } from '../components/GlassCard';
import { useUserStore } from '../store/userStore';
import { STATUS_STYLE } from '../data/constants';
import { getLatestAssessment, getPremiumInsights } from '../lib/api';

const DOMAIN_META = {
  hormonal_transition: { title: 'Hormonal Transition', icon: 'fa-droplet', color: '#8B5CF6' },
  metabolic_strain: { title: 'Metabolic Strain', icon: 'fa-fire', color: '#F97316' },
  sleep_recovery: { title: 'Sleep & Recovery', icon: 'fa-moon', color: '#6366F1' },
  mental_wellbeing: { title: 'Mental Wellbeing', icon: 'fa-brain', color: '#14B8A6' },
};

function renderInsightValue(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? renderInsightValue(x) : String(x))).join(' · ');
  if (typeof v === 'object') {
    return Object.entries(v)
      .map(([k, val]) => `${k.replace(/_/g, ' ')}: ${renderInsightValue(val)}`)
      .join(' — ');
  }
  return String(v);
}

export default function AssessmentResults() {
  const navigate = useNavigate();
  const { user, isPremiumUser, checkPremiumStatus } = useUserStore();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [premium, setPremium] = useState(null);
  const [loadingPremium, setLoadingPremium] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    checkPremiumStatus();
    getLatestAssessment(user.id).then((res) => setA(res)).finally(() => setLoading(false));
  }, [user?.id]); // eslint-disable-line

  const loadPremium = async () => {
    setLoadingPremium(true);
    try {
      const res = await getPremiumInsights(user.id);
      setPremium(res);
    } finally {
      setLoadingPremium(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center" style={{ background: 'var(--aura-bg)' }}><i className="fa-solid fa-circle-notch fa-spin text-2xl text-[var(--aura-purple)]" /></div>;
  }

  if (!a) {
    return (
      <div className="min-h-screen grid place-items-center px-4" style={{ background: 'var(--aura-bg)' }}>
        <GlassCard className="p-10 text-center max-w-md">
          <div className="grid place-items-center w-16 h-16 rounded-2xl mx-auto mb-4 bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]"><i className="fa-solid fa-wand-magic-sparkles text-2xl" /></div>
          <h2 className="font-display text-2xl mb-2">No assessment yet</h2>
          <p className="text-[var(--aura-ink-soft)] mb-5">Take your Precision Health Assessment to reveal your patterns.</p>
          <button onClick={() => navigate('/assessment')} className="aura-btn-gradient text-white font-semibold rounded-full px-6 py-3">Start assessment</button>
        </GlassCard>
      </div>
    );
  }

  const domains = a.domains || {};
  const overall = (() => {
    const vals = Object.values(domains).map((d) => Math.min(100, d.score || 0));
    if (!vals.length) return 74;
    const avg = vals.reduce((x, y) => x + y, 0) / vals.length;
    return Math.max(28, Math.min(94, Math.round(100 - avg * 0.6)));
  })();

  return (
    <div className="min-h-screen" style={{ background: 'var(--aura-bg)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)]"><i className="fa-solid fa-arrow-left" /></button>
          <h1 className="font-display text-2xl sm:text-3xl">Your precision insights</h1>
        </div>

        <GlassCard className="p-6 sm:p-8 mb-5 flex flex-col sm:flex-row items-center gap-6">
          <ScoreRing value={overall} size={140} label="Wellbeing" />
          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-display text-xl mb-2">The connected picture</h2>
            <p className="text-[var(--aura-ink-soft)] leading-relaxed">{a.primary_insight}</p>
            <p className="text-xs text-[var(--aura-ink-soft)]/80 mt-3">Your wellbeing score reflects where support could help most — it moves with you as your patterns settle.</p>
          </div>
        </GlassCard>

        {/* Domain breakdown */}
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          {Object.entries(DOMAIN_META).map(([key, meta]) => {
            const d = domains[key];
            if (!d) return null;
            const st = STATUS_STYLE[d.status] || STATUS_STYLE.low;
            return (
              <GlassCard key={key} className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: `${meta.color}18`, color: meta.color }}><i className={`fa-solid ${meta.icon}`} /></div>
                    <span className="font-semibold">{meta.title}</span>
                  </div>
                  <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                </div>
                <p className="text-sm text-[var(--aura-ink-soft)] leading-relaxed">{d.what_this_means}</p>
                {d.contributing_signals?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {d.contributing_signals.slice(0, 4).map((s, i) => (
                      <span key={i} className="text-[11px] rounded-full px-2.5 py-1 bg-[var(--aura-purple)]/8 text-[var(--aura-purple)]">{s.signal}</span>
                    ))}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>

        {/* Overlap connections */}
        {a.overlap_connections?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">How your domains connect</h3>
            <div className="flex flex-col gap-3">
              {a.overlap_connections.map((o, i) => (
                <GlassCard key={i} className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {o.domains.map((dn, j) => (
                      <React.Fragment key={j}>
                        <span className="text-sm font-semibold text-[var(--aura-purple)]">{dn}</span>
                        {j < o.domains.length - 1 && <i className="fa-solid fa-link text-xs text-[var(--aura-ink-soft)]" />}
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="text-sm text-[var(--aura-ink-soft)] leading-relaxed mb-2">{o.connection}</p>
                  <p className="text-sm font-medium text-[var(--aura-teal)]"><i className="fa-solid fa-lightbulb mr-1" /> {o.implication}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {/* Lifestyle modifiers */}
        {a.lifestyle_modifiers?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-display text-xl mb-3">Lifestyle factors at play</h3>
            <div className="flex flex-col gap-3">
              {a.lifestyle_modifiers.map((m, i) => (
                <GlassCard key={i} className="p-5">
                  <div className="font-semibold mb-1">{m.factor}</div>
                  <p className="text-sm text-[var(--aura-ink-soft)]">{m.impact}</p>
                  <p className="text-sm text-[var(--aura-teal)] mt-1"><i className="fa-solid fa-arrow-right mr-1" /> {m.suggestion}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {/* Premium */}
        <GlassCard className="p-6 mb-5" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(236,72,153,0.06))' }}>
          <div className="flex items-center gap-2 mb-2">
            <i className="fa-solid fa-lock text-[var(--aura-purple)]" />
            <h3 className="font-display text-xl">Unlock what’s really driving this</h3>
          </div>
          {!premium && (
            <>
              <p className="text-[var(--aura-ink-soft)] mb-4">Premium reveals your hormonal profile, cortisol patterns, metabolic response and a root-cause sleep analysis.</p>
              {isPremiumUser ? (
                <button data-testid="load-premium-btn" onClick={loadPremium} disabled={loadingPremium} className="aura-btn-gradient text-white font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-70">
                  {loadingPremium ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-unlock" />} Reveal premium insights
                </button>
              ) : (
                <button data-testid="upgrade-cta-btn" onClick={() => navigate('/upgrade')} className="aura-btn-gradient text-white font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2">
                  <i className="fa-solid fa-crown" /> Upgrade to unlock
                </button>
              )}
            </>
          )}
          {premium && !premium.locked && premium.premium_insights && (
            <div className="space-y-3 mt-2">
              {Object.entries(premium.premium_insights).map(([k, v]) => (
                <div key={k}>
                  <div className="text-sm font-semibold capitalize text-[var(--aura-purple)]">{k.replace(/_/g, ' ')}</div>
                  <p className="text-sm text-[var(--aura-ink-soft)]">{renderInsightValue(v)}</p>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        <button onClick={() => navigate('/biomarkers')} className="w-full rounded-2xl py-3.5 border border-[var(--aura-line)] text-[var(--aura-purple)] font-semibold flex items-center justify-center gap-2 hover:bg-[var(--aura-purple)]/6 transition-base">
          <i className="fa-solid fa-vial" /> View my blood results
        </button>
      </div>
    </div>
  );
}
